<template>
    <activix-modal
        size="xl"
        :opened="opened"
        :portal="portal"
        @close="close"
        @closed="onClosed"
        @open="onOpen"
    >
        <template #header>
            <h4 class="modal-title">
                {{ $t('searchLead.title') }}
            </h4>
        </template>

        <template #body>
            <search-lead-table
                :leads="leads"
                :lead-loading="selectedLead"
                :potential-lead-count="potentialLeadCount"
                :potential-lead-count-is-capped="potentialLeadCountIsCapped"
                @select-lead="onSelectLead"
                @search="onSearch"
            />
        </template>
    </activix-modal>
</template>

<script>
    import { useUserStore } from '@/store/modules/user/store.js';
    import { SearchContext } from '@/components/modals/SearchLeads.types.ts';
    import SearchLeadTable from '../calendar/SearchLeadTable.vue';
    import Lead from '../../entities/Lead.js';

    // Pinia
    import { useAccountStore } from '../../store/modules/account/store.js';

    export default {
        emits: ['update:opened', 'select-lead'],

        components: { SearchLeadTable },

        props: {
            opened: {
                type: Boolean,
                required: true,
                default: false,
            },
            portal: {
                type: String,
                default: 'modal-1',
            },
            selectedAccount: {
                type: Number,
                default: null,
            },
            searchContext: {
                type: String,
                default: SearchContext.DEFAULT,
                validator: (value) => Object.values(SearchContext).includes(value),
            },
        },
        data() {
            return {
                leads: Array(10).fill(new Lead()),
                selectedLead: null,
                searchString: '',
                potentialLeadCount: null,
                potentialLeadCountIsCapped: false,
            };
        },

        computed: {
            authUser() {
                return useUserStore().authUser;
            },

            contextAccount() {
                return useAccountStore().contextAccount;
            },

            isLoading() {
                return this.$wait.is('fetching.calendarLead');
            },
        },

        watch: {
            isLoading(newValue, oldValue) {
                if (newValue || !oldValue) {
                    return;
                }

                this.close();
            },
        },

        methods: {
            async fetch() {
                if (!this.contextAccount.id) {
                    return;
                }

                this.$wait.start('fetching.taskEventLeads');

                let path;
                switch (this.searchContext) {
                    case SearchContext.SMS:
                        path = 'v1/conversations/get-leads';
                        break;
                    case SearchContext.DEFAULT:
                    default:
                        path = 'v1/task-events/get-leads';
                        break;
                }

                const response = await this.$axios.get(path, {
                    params: {
                        accountId: this.selectedAccount || this.contextAccount.id,
                        search: this.searchString,
                    },
                });

                this.leads = response.data.data.map((lead) => new Lead(lead));
                this.potentialLeadCount = response.data.meta?.potential_lead_count ?? null;
                this.potentialLeadCountIsCapped =
                    response.data.meta?.potential_lead_count_is_capped ?? false;

                this.$wait.end('fetching.taskEventLeads');
            },

            close() {
                this.$emit('update:opened', false);
            },

            onSelectLead(leadId) {
                this.selectedLead = leadId;
                this.$emit('select-lead', leadId);
            },

            onSearch(searchString) {
                this.searchString = searchString;
                this.fetch();
            },

            onOpen() {
                this.searchString = '';
                this.fetch();
            },

            onClosed() {
                this.selectedLead = null;
            },
        },
    };
</script>
